import { FC, useEffect, useMemo, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import { InfoCard } from "./InfoCard"
import { getLocationItems } from "./utils"
import { VitalInfoCard } from "./VitalInfoCard"
import { InmateVitalsResponse } from "@/api/InmatesService"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import { AppDispatch, RootState } from "@/store"
import {
   selectMeasurementSystem,
   selectTimezone,
} from "@/store/facilitiesSlice"
import { fetchInmateVitals, resetInmateVitals } from "@/store/inmatesSlice"
import { getErrorMessage, localizeDateTime } from "@/utils/index"
import {
   convertTemperature,
   getTemperatureUnit,
} from "@/utils/measurementSystem"

const Summary: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const [lastRead, setLastRead] = useState<Dayjs | null>(null)
   const timezone = useSelector(selectTimezone)
   const measurementSystem = useSelector(selectMeasurementSystem)
   const { id } = useParams<{ id: string }>()
   const { inmateVitals, loadingInmateVitals, errorInmateVitals } = useSelector(
      ({ inmatesReducer }: RootState) => inmatesReducer
   )
   const { facilityDetails } = useSelector(
      ({ facilitiesReducer }: RootState) => facilitiesReducer
   )

   const { averages, heartRate, spo2, temperature, anchorMac } =
      inmateVitals || ({} as InmateVitalsResponse)

   const heartRateAverage = averages?.heartRate ?? null
   const spo2Average = averages?.spo2 ?? null
   const temperatureAverage = averages?.temperature ?? null

   useEffect(() => {
      async function fetchVitalsData() {
         try {
            if (!id) return
            await dispatch(fetchInmateVitals(id))
               .unwrap()
               .then(() => {
                  setLastRead(dayjs())
               })
         } catch (err) {
            console.error("Inmate vitals fetch failed:", err)
         }
      }

      fetchVitalsData()
      const interval = setInterval(fetchVitalsData, 5000)

      return () => {
         clearInterval(interval)
         dispatch(resetInmateVitals())
      }
   }, [dispatch, id])

   const formatTemperature = (celsius: number | null): string =>
      convertTemperature(celsius, measurementSystem)?.toFixed(1) ?? "-"

   const locationItems = useMemo(
      () => getLocationItems(anchorMac, facilityDetails?.levels),
      [anchorMac, facilityDetails?.levels]
   )

   return (
      <Grid className="inmate-detail-overview__summary">
         {!loadingInmateVitals && errorInmateVitals ? (
            <GridItem size={12}>{getErrorMessage(errorInmateVitals)}</GridItem>
         ) : (
            <>
               {lastRead && (
                  <span className="inmate-detail-overview__last-read">
                     Last read:&nbsp;
                     <span>{localizeDateTime(lastRead, timezone)}</span>
                  </span>
               )}
               <GridItem
                  size={4}
                  className="inmate-detail-overview__section"
               >
                  <VitalInfoCard
                     iconName="heartRate"
                     title="Heart Rate"
                     value={heartRate ?? "-"}
                     valueUnit="BPM"
                     avgValue={
                        heartRateAverage !== null
                           ? heartRateAverage.toFixed(1)
                           : "-"
                     }
                     avgLabel="avg rate"
                     avgSubLabel="last 24 hr"
                  />
               </GridItem>
               <GridItem
                  size={4}
                  className="inmate-detail-overview__section"
               >
                  <VitalInfoCard
                     iconName="oxygenLevel"
                     title="Oxygen Level"
                     value={spo2 ?? "-"}
                     valueUnit="%"
                     avgValue={
                        spo2Average !== null ? spo2Average.toFixed(1) : "-"
                     }
                     avgLabel="avg rate"
                     avgSubLabel="last 24 hr"
                  />
               </GridItem>
               <GridItem
                  size={4}
                  className="inmate-detail-overview__section"
               >
                  <VitalInfoCard
                     iconName="temperature"
                     title="Temperature"
                     value={formatTemperature(temperature)}
                     valueUnit={getTemperatureUnit(measurementSystem)}
                     avgValue={formatTemperature(temperatureAverage)}
                     avgLabel="avg rate"
                     avgSubLabel="last 24 hr"
                  />
               </GridItem>
               {locationItems.length > 0 && (
                  <GridItem
                     size={3}
                     className="inmate-detail-overview__section"
                  >
                     <InfoCard
                        title="Location"
                        iconName="position"
                        items={locationItems}
                     />
                  </GridItem>
               )}
            </>
         )}
      </Grid>
   )
}

export default Summary
